import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  ShoppingCart, 
  TrendingUp, 
  Users, 
  DollarSign,
  Package,
  Calendar,
  BarChart3,
  PieChart,
  Activity
} from 'lucide-react';
import { 
  LineChart as RechartsLineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend,
  ResponsiveContainer, 
  BarChart, 
  Bar,
  PieChart as RechartsPieChart,
  Cell,
  Pie
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CartAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30d');

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cart/analytics`, {
        params: { time_range: timeRange },
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching cart analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatPercentage = (value) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Cart & Checkout Analytics
          </h1>
          <p className="text-gray-600">
            Track cart performance, conversion rates, and revenue metrics
          </p>
        </div>
        
        <div className="flex gap-2">
          {['7d', '30d', '90d'].map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-3 py-1 text-sm rounded ${
                timeRange === range 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {range}
            </button>
          ))}
        </div>
      </div>

      {analytics && (
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="conversion">Conversion</TabsTrigger>
            <TabsTrigger value="revenue">Revenue</TabsTrigger>
            <TabsTrigger value="products">Products</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <ShoppingCart className="h-8 w-8 text-blue-600" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Total Carts</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {analytics.overview?.total_carts || 0}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <TrendingUp className="h-8 w-8 text-green-600" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Conversion Rate</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatPercentage(analytics.overview?.conversion_rate)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <DollarSign className="h-8 w-8 text-purple-600" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Avg Cart Value</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatCurrency(analytics.overview?.average_cart_value)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <Package className="h-8 w-8 text-orange-600" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Items per Cart</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {(analytics.overview?.items_per_cart || 0).toFixed(1)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Cart Status Distribution */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <PieChart className="h-5 w-5 mr-2" />
                    Cart Status Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics.cart_status && (
                    <ResponsiveContainer width="100%" height={300}>
                      <RechartsPieChart>
                        <Pie
                          data={analytics.cart_status}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {analytics.cart_status.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <BarChart3 className="h-5 w-5 mr-2" />
                    Daily Cart Activity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics.daily_activity && (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={analytics.daily_activity}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="carts_created" fill="#3B82F6" name="Created" />
                        <Bar dataKey="carts_completed" fill="#10B981" name="Completed" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Conversion Tab */}
          <TabsContent value="conversion" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Conversion Funnel</CardTitle>
                  <CardDescription>Track user journey from cart creation to checkout</CardDescription>
                </CardHeader>
                <CardContent>
                  {analytics.conversion_funnel && (
                    <div className="space-y-4">
                      {analytics.conversion_funnel.map((step, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${
                              index === 0 ? 'bg-blue-600' : 
                              index === 1 ? 'bg-green-600' : 
                              index === 2 ? 'bg-purple-600' : 'bg-orange-600'
                            }`}>
                              {index + 1}
                            </div>
                            <span className="ml-3 font-medium">{step.name}</span>
                          </div>
                          <div className="flex items-center">
                            <span className="mr-4 text-lg font-bold">{step.count}</span>
                            <Badge variant="outline">
                              {formatPercentage(step.conversion_rate)}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Abandonment Reasons</CardTitle>
                  <CardDescription>Why users abandon their carts</CardDescription>
                </CardHeader>
                <CardContent>
                  {analytics.abandonment_reasons && (
                    <div className="space-y-3">
                      {analytics.abandonment_reasons.map((reason, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                          <span className="text-sm">{reason.reason}</span>
                          <Badge variant="secondary">{reason.count}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Revenue Tab */}
          <TabsContent value="revenue" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <DollarSign className="h-5 w-5 mr-2" />
                  Revenue Trends
                </CardTitle>
              </CardHeader>
              <CardContent>
                {analytics.revenue_trends && (
                  <ResponsiveContainer width="100%" height={400}>
                    <RechartsLineChart data={analytics.revenue_trends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="subscription_revenue" 
                        stroke="#3B82F6" 
                        strokeWidth={2}
                        name="Subscription Revenue"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="addon_revenue" 
                        stroke="#10B981" 
                        strokeWidth={2}
                        name="Add-on Revenue"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="total_revenue" 
                        stroke="#8B5CF6" 
                        strokeWidth={3}
                        name="Total Revenue"
                      />
                    </RechartsLineChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Top Add-ons</CardTitle>
                  <CardDescription>Most popular add-on services</CardDescription>
                </CardHeader>
                <CardContent>
                  {analytics.top_addons && (
                    <div className="space-y-3">
                      {analytics.top_addons.slice(0, 5).map((addon, index) => (
                        <div key={index} className="flex items-center justify-between p-3 border rounded">
                          <div>
                            <div className="font-medium">{addon.name}</div>
                            <div className="text-sm text-gray-600">{addon.purchases} purchases</div>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold">{formatCurrency(addon.revenue)}</div>
                            <Badge variant="outline">{addon.category}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Subscription Upgrades</CardTitle>
                  <CardDescription>Subscription tier changes through cart</CardDescription>
                </CardHeader>
                <CardContent>
                  {analytics.subscription_upgrades && (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={analytics.subscription_upgrades}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="tier_name" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="upgrades" fill="#8B5CF6" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default CartAnalytics;